import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { Observable, catchError, throwError } from 'rxjs';
import { CustomNotificationService } from '../services/custom-notification.service';
import { AuthenticationService } from './authentication.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
    constructor(
        private notificationService: CustomNotificationService,
        private router: Router,
        private authenticationService: AuthenticationService
    ) {}

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(request).pipe(
            catchError((err: any) => {
                // check if it's a Blob and parse it - see https://stackoverflow.com/questions/49479959/angular-5-manage-http-get-with-blob-response-and-json-errors
                if (err instanceof HttpErrorResponse && err.error instanceof Blob && err.error.type === 'application/json') {
                    return this.processBlobErr(err);
                }

                if (err.status === 401) {
                    // auto logout if 401 response returned from api
                    if (!err.url || !err.url.endsWith('login')) {
                        this.authenticationService.logout();
                        this.router.navigate(['/login'], {
                            queryParams: { returnUrl: this.router.url }
                        });
                        this.notificationService.handleError('Token expired or invalid. Please login again.');

                        return throwError(null);
                    }
                    if (err.message) {
                        this.notificationService.handleError('Invalid credentials');

                        return throwError(err.message);
                    }
                }

                if (err.error) {
                    const errorMessage = this.getErrorMessage(err.error);
                    if (errorMessage) {
                        if (errorMessage.includes('417 : [')) {
                            const messageStr = errorMessage.split('417 : [')[1];
                            const messageStrObj = JSON.parse(messageStr.substring(0, messageStr.length - 1));
                            if (messageStrObj.message) {
                                this.notificationService.handleError(messageStrObj.message);

                                return throwError(messageStrObj.message);
                            }
                        }

                        this.notificationService.handleError(errorMessage);

                        return throwError(() => errorMessage);
                    }
                }

                const error = err.statusText;
                this.notificationService.handleError(error);

                return throwError(() => error);
            })
        );
    }

    getErrorMessage(errorResponse: any) {
        try {
            return errorResponse.message;
        } catch (e) {
            console.error('error converting response: ', e);
        }
    }

    processBlobErr(err: HttpErrorResponse): Promise<any> {
        // https://github.com/angular/angular/issues/19888
        // When request of type Blob, the error is also in Blob instead of object of the json data
        return new Promise<any>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = (e: Event) => {
                try {
                    const errmsg = JSON.parse((<any>e.target).result);
                    reject(
                        new HttpErrorResponse({
                            error: errmsg,
                            headers: err.headers,
                            status: err.status,
                            statusText: err.statusText,
                            url: err.url ?? undefined
                        })
                    );
                } catch (e) {
                    console.error('error getting blob: ', e);
                    reject(err);
                }
            };
            reader.onerror = (e) => {
                reject(err);
            };
            reader.readAsText(err.error);
        });
    }
}
